import { useMemo } from "react";
import { useOpsStore } from "../state/opsStore";
import type { BatchLive } from "../state/opsStore";
import "./SlaWatchlist.css";

/**
 * Phase 18 WU-18.D6 — SLA Watchlist panel (top-left of Ops Console).
 *
 * Renders batches in progress sorted by urgency (elapsed / slaTargetMs).
 * Safety Orange left stripe at 75% elapsed, Emergency Red at 100%+.
 * Click row → batch trail. Empty state when no batches are in play.
 */

function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function pctOf(b: BatchLive): number | null {
  if (!b.slaTargetMs || b.slaTargetMs <= 0) return null;
  return Math.round((b.elapsedMs / b.slaTargetMs) * 100);
}

interface SlaWatchlistProps {
  onBatchClick?: (batchId: string) => void;
}

export function SlaWatchlist({ onBatchClick }: SlaWatchlistProps) {
  const batchesById = useOpsStore((s) => s.batches);
  const sorted = useMemo(() => {
    const arr = Object.values(batchesById);
    arr.sort((a, b) => {
      const pa = a.slaTargetMs ? a.elapsedMs / a.slaTargetMs : -1;
      const pb = b.slaTargetMs ? b.elapsedMs / b.slaTargetMs : -1;
      return pb - pa;
    });
    return arr;
  }, [batchesById]);

  if (sorted.length === 0) {
    return (
      <div
        className="ops-sla-watchlist__empty"
        data-testid="ops-sla-watchlist-empty"
      >
        No batches in progress.
      </div>
    );
  }

  return (
    <ul className="ops-sla-watchlist" role="list" data-testid="ops-sla-watchlist">
      {sorted.map((b) => {
        const pct = pctOf(b);
        const rowClass = `ops-sla-watchlist__row ops-sla-watchlist__row--${b.slaStatus}`;
        const pctClass = `ops-sla-watchlist__pct ops-sla-watchlist__pct--${b.slaStatus}`;
        return (
          <li
            key={b.batchId}
            role="button"
            tabIndex={0}
            className={rowClass}
            data-batch-id={b.batchId}
            onClick={() => onBatchClick?.(b.batchId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onBatchClick?.(b.batchId);
              }
            }}
          >
            <span className="ops-sla-watchlist__name" title={b.jobTypeName}>
              {b.jobTypeName}
            </span>
            <span className="ops-sla-watchlist__step">
              {b.currentStep ?? "—"}
            </span>
            <span className="ops-sla-watchlist__elapsed">
              {formatElapsed(b.elapsedMs)}
            </span>
            <span className={pctClass}>{pct !== null ? `${pct}%` : "—"}</span>
          </li>
        );
      })}
    </ul>
  );
}
